/**
 * Onboarding step registry — the single source of truth for the wizard.
 *
 * Step keys, ordering, dependencies, and grouping mirror the build plan
 * (docs/onboarding/WIZARD-BUILD-PLAN.md §3). New-run creation seeds one
 * onboarding_tasks row per entry; the run page renders one card per entry.
 *
 * Steps without an implemented card render as stubs ("coming soon") — each
 * vertical slice flips its step to implemented as it ships.
 */

export type OnboardingStepStatus =
  | 'pending'
  | 'in_progress'
  | 'waiting_on_client'
  | 'waiting_on_vendor'
  | 'needs_review'
  | 'done'
  | 'error'
  | 'skipped'

export type OnboardingStepGroup =
  | 'Integrations'
  | 'Attribution'
  | 'Domain'
  | 'Verify'

export interface OnboardingStepDefinition {
  readonly key: string
  readonly label: string
  readonly description: string
  readonly group: OnboardingStepGroup
  readonly dependsOn: readonly string[]
  /** True once the step's vertical slice has shipped a working card. */
  readonly implemented: boolean
}

// Domain-first ordering: the Domain group (including the Stape provision
// chain, which produces the br/load.br CNAME targets) leads the flow so the
// client's DNS records go out immediately and propagation runs concurrently
// with the rest of the onboarding. Dependencies are unchanged.
export const ONBOARDING_STEPS: readonly OnboardingStepDefinition[] = [
  {
    key: 'stape_domain',
    label: 'Stape domain',
    description: 'Create the Stape custom domain + CDN; returns CNAME targets.',
    group: 'Domain',
    dependsOn: [],
    implemented: true,
  },
  {
    key: 'stape_loader',
    label: 'Stape custom loader',
    description: 'Generate the custom loader script path and token.',
    group: 'Domain',
    dependsOn: ['stape_domain'],
    implemented: true,
  },
  {
    key: 'stape_settings',
    label: 'Stape settings',
    description: 'Write the stape_* account settings (https, no trailing slash).',
    group: 'Domain',
    dependsOn: ['stape_loader'],
    implemented: true,
  },
  {
    key: 'vercel_domain',
    label: 'Vercel domain',
    description: 'Add book.{domain} to the viewer project on Vercel.',
    group: 'Domain',
    dependsOn: [],
    implemented: true,
  },
  {
    key: 'generate_dns_email',
    label: 'DNS instruction email',
    description:
      'Build the combined 3-record email (book / br / load.br) for the client.',
    group: 'Domain',
    dependsOn: ['stape_domain', 'vercel_domain'],
    implemented: true,
  },
  {
    key: 'verify_dns',
    label: 'DNS verification',
    description: 'Poller resolves the 3 CNAMEs and flips records green.',
    group: 'Domain',
    dependsOn: ['generate_dns_email'],
    implemented: true,
  },
  {
    key: 'ga4_property',
    label: 'GA4 property',
    description: 'Create GA4 property + web stream; capture the Measurement ID.',
    group: 'Attribution',
    dependsOn: [],
    implemented: true,
  },
  {
    key: 'ads_conversions',
    label: 'Google Ads conversions',
    description:
      'Fetch the AW conversion ID and create the standard conversion actions.',
    group: 'Attribution',
    dependsOn: [],
    implemented: true,
  },
  {
    key: 'conversion_labels',
    label: 'Conversion labels',
    description:
      'Capture the per-event conversion labels (gads_label_*) the viewer fires.',
    group: 'Attribution',
    dependsOn: ['ads_conversions'],
    implemented: true,
  },
  {
    key: 'microsoft_uet',
    label: 'Microsoft Ads UET tag',
    description: 'Paste the Microsoft Advertising UET Tag ID.',
    group: 'Attribution',
    dependsOn: [],
    implemented: true,
  },
  {
    key: 'gtm_server_url',
    label: 'GTM server URL',
    description: 'Append the br.{domain} URL to the server GTM container.',
    group: 'Attribution',
    dependsOn: ['stape_domain'],
    implemented: true,
  },
  {
    key: 'stripe_connect',
    label: 'Stripe Connect',
    description:
      'Client authorizes the platform via Stripe Connect OAuth — no keys shared.',
    group: 'Integrations',
    dependsOn: [],
    implemented: true,
  },
  {
    key: 'stripe_platform_webhook',
    label: 'Stripe platform webhook',
    description:
      'One Connect webhook endpoint for all clients (created once, reused).',
    group: 'Integrations',
    dependsOn: [],
    implemented: true,
  },
  {
    key: 'limo_anywhere',
    label: 'Limo Anywhere',
    description: 'Validate credentials, fetch vehicle types, confirm mapping.',
    group: 'Integrations',
    dependsOn: [],
    implemented: true,
  },
  {
    key: 'ghl_discovery',
    label: 'GoHighLevel discovery',
    description: 'Fetch pipelines and stages for the config dropdowns.',
    group: 'Integrations',
    dependsOn: [],
    implemented: true,
  },
  {
    key: 'ghl_config',
    label: 'GoHighLevel config',
    description: 'Write API key, location, pipeline, and stage IDs.',
    group: 'Integrations',
    dependsOn: ['ghl_discovery'],
    implemented: true,
  },
  {
    key: 'zapier_link',
    label: 'Zapier link',
    description: 'Confirm the shared-Zap webhook and account_slug payload field.',
    group: 'Integrations',
    dependsOn: [],
    implemented: true,
  },
  {
    key: 'smoke_test',
    label: 'Smoke test',
    description:
      'Run Playwright booking scenarios + dataLayer asserts on the live domain.',
    group: 'Verify',
    dependsOn: ['stripe_connect', 'verify_dns', 'gtm_server_url'],
    implemented: true,
  },
  {
    key: 'verify_ads_active',
    label: 'Ads conversions active',
    description:
      'Refresh to confirm the conversion actions are recording conversions.',
    group: 'Verify',
    dependsOn: ['conversion_labels'],
    implemented: true,
  },
]

export interface NewRunInputs {
  readonly mainDomain: string
  readonly adsCustomerId: string | null
  readonly timezone: string
}

/** Form inputs land in the params of the steps that consume them. */
const STEP_PARAMS: Record<string, (inputs: NewRunInputs) => Record<string, string>> = {
  vercel_domain: ({ mainDomain }) => ({ mainDomain }),
  stape_domain: ({ mainDomain }) => ({ mainDomain }),
  generate_dns_email: ({ mainDomain }) => ({ mainDomain }),
  verify_dns: ({ mainDomain }) => ({ mainDomain }),
  ga4_property: ({ mainDomain, timezone }) => ({ mainDomain, timezone }),
  ads_conversions: ({ adsCustomerId }): Record<string, string> =>
    adsCustomerId ? { adsCustomerId } : {},
  gtm_server_url: ({ mainDomain }) => ({ mainDomain }),
  smoke_test: ({ mainDomain }) => ({ mainDomain }),
}

export interface OnboardingTaskSeed {
  readonly step: string
  readonly depends_on: string[]
  readonly params: Record<string, string>
}

export function buildTaskSeeds(inputs: NewRunInputs): OnboardingTaskSeed[] {
  return ONBOARDING_STEPS.map((step) => ({
    step: step.key,
    depends_on: [...step.dependsOn],
    params: STEP_PARAMS[step.key]?.(inputs) ?? {},
  }))
}

export function getStepDefinition(
  key: string
): OnboardingStepDefinition | undefined {
  return ONBOARDING_STEPS.find((step) => step.key === key)
}

export const STEP_STATUS_CONFIG: Record<
  OnboardingStepStatus,
  { label: string; className: string }
> = {
  pending: { label: 'Pending', className: 'bg-muted text-muted-foreground' },
  in_progress: { label: 'In progress', className: 'bg-blue-100 text-blue-800' },
  waiting_on_client: {
    label: 'Waiting on client',
    className: 'bg-amber-100 text-amber-800',
  },
  waiting_on_vendor: {
    label: 'Waiting on vendor',
    className: 'bg-amber-100 text-amber-800',
  },
  needs_review: {
    label: 'Needs review',
    className: 'bg-orange-100 text-orange-800',
  },
  done: { label: 'Done', className: 'bg-green-100 text-green-800' },
  error: { label: 'Error', className: 'bg-red-100 text-red-800' },
  skipped: { label: 'Skipped', className: 'bg-muted text-muted-foreground' },
}

export const ONBOARDING_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
] as const
